import { PaginatedDocuments } from 'models/shared/types/pagination';
import { EvidenceDocumentType } from 'models/shared/types/documents';
import { deletes, get, post, put } from 'models/shared/helpers/axiosInstance';
import { buildPathWithQueryParams } from 'models/shared/helpers/utils';
import {
	CreditLineOffer,
	LoanOffer,
} from 'models/loan-offer/controllers/loan-offer.controller';
import {
	CreateCreditLineFinancialFormDto,
	CreateFullPaymentFinancialFormDto,
	CreateLoanFinancialFormDto,
	FinancialFormFilter,
	FullPaymentFinancialForm,
	GenericFinancialForm,
	LoanFinancialFormWithExtraFields,
	PopulatedCreditLineFinancialForm,
	PopulatedFullPaymentFinancialForm,
	ScheduleAppointmentDto,
	TaxDataDto,
	UpdateCreditLineFinancialFormDto,
	UpdateFinancialFormDto,
	UpdateFullPaymentFinancialFormDto,
} from '../interfaces/financial-form.interfaces';

const baseFinancialFormPath = '/v1/financial-form';

export const createLoanFinancialForm = async (
	dto: CreateLoanFinancialFormDto,
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const res = await post<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/loan`,
			dto,
		);

		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const createFullPaymentFinancialForm = async (
	dto: CreateFullPaymentFinancialFormDto,
): Promise<PopulatedFullPaymentFinancialForm> => {
	try {
		const res = await post<PopulatedFullPaymentFinancialForm>(
			`${baseFinancialFormPath}/full-payment`,
			dto,
		);

		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const getFinancialForm = async (
	formId: string,
): Promise<GenericFinancialForm> => {
	try {
		const res = await get<GenericFinancialForm>(
			`${baseFinancialFormPath}/loan/${formId}`,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const updateFinancialForm = async (
	formId: string,
	dto: UpdateFinancialFormDto,
): Promise<GenericFinancialForm> => {
	try {
		const res = await put<GenericFinancialForm>(
			`${baseFinancialFormPath}/${formId}`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};
export const canCreateFinancialFormByCurp = async (
	curp: string,
): Promise<{ canCreateByCurp: boolean; whitelistName: string | null }> => {
	try {
		const res = await get<{
			canCreateByCurp: boolean;
			whitelistName: string | null;
		}>(
			buildPathWithQueryParams(`${baseFinancialFormPath}/can-create`, {
				curp,
			}),
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const createCreditLineFinancialForm = async (
	dto: CreateCreditLineFinancialFormDto,
): Promise<PopulatedCreditLineFinancialForm> => {
	try {
		const res = await post<PopulatedCreditLineFinancialForm>(
			`${baseFinancialFormPath}/credit-line`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const updateCreditLineFinancialForm = async (
	formId: string,
	dto: UpdateCreditLineFinancialFormDto,
): Promise<PopulatedCreditLineFinancialForm> => {
	try {
		const res = await put<PopulatedCreditLineFinancialForm>(
			`${baseFinancialFormPath}/credit-line/${formId}`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const updateFullPaymentFinancialForm = async (
	formId: string,
	dto: UpdateFullPaymentFinancialFormDto,
): Promise<GenericFinancialForm> => {
	try {
		const res = await put<GenericFinancialForm>(
			`${baseFinancialFormPath}/full-payment/${formId}`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const findAllFinancialForms = async (
	dto: FinancialFormFilter,
): Promise<PaginatedDocuments<GenericFinancialForm>> => {
	try {
		const res = await get<PaginatedDocuments<GenericFinancialForm>>(
			buildPathWithQueryParams(baseFinancialFormPath, dto),
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const findAllFinancialFormsOzoner = async (
	ozonerId: string,
): Promise<GenericFinancialForm[]> => {
	try {
		const res = await get<GenericFinancialForm[]>(
			`${baseFinancialFormPath}/ozoner/${ozonerId}`,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const uploadFileFinancialForm = async (
	formId: string,
	file: File,
	type: EvidenceDocumentType,
	alternativeName = '',
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const data = new FormData();
		data.append('name', alternativeName !== '' ? alternativeName : file.name);
		data.append('file', file);

		const res = await put<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/${formId}/document/${type}`,
			data,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			},
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const deleteFileFinancialForm = async (
	formId: string,
	docId: string,
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const res = await deletes<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/${formId}/document/${docId}`,
		);

		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const uploadTaxSituationFinancialForm = async (
	formId: string,
	file: File,
	alternativeName = '',
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const data = new FormData();
		data.append('name', alternativeName !== '' ? alternativeName : file.name);
		data.append('file', file);

		const res = await put<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/${formId}/tax-situation`,
			data,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			},
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const updateTaxDataFinancialForm = async (
	formId: string,
	dto: TaxDataDto,
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const res = await put<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/${formId}/tax-data`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};
export const uploadFileFullPaymentFinancialForm = async (
	formId: string,
	file: File,
	type: EvidenceDocumentType,
	alternativeName = '',
): Promise<FullPaymentFinancialForm> => {
	try {
		const data = new FormData();
		data.append('name', alternativeName !== '' ? alternativeName : file.name);
		data.append('file', file);

		const res = await put<FullPaymentFinancialForm>(
			`${baseFinancialFormPath}/${formId}/document/fp/${type}`,
			data,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
			},
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const deleteFileFullPaymentFinancialForm = async (
	formId: string,
	docId: string,
): Promise<FullPaymentFinancialForm> => {
	try {
		const res = await deletes<FullPaymentFinancialForm>(
			`${baseFinancialFormPath}/${formId}/document/fp/${docId}`,
		);

		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const scheduleAppointment = async (
	formId: string,
	dto: ScheduleAppointmentDto,
) => {
	try {
		const res = await post(
			`${baseFinancialFormPath}/schedule-appointment/${formId}`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const cancelAppointment = async (formId: string) => {
	try {
		const res = await post(
			`${baseFinancialFormPath}/cancel-appointment/${formId}`,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const calculateAndSaveOffers = async (
	formId: string,
	dto: {
		ticketPrice?: number;
		downPaymentAmount?: number;
		loanDuration?: number;
	},
): Promise<LoanOffer> => {
	try {
		const res = await post<LoanOffer>(
			`${baseFinancialFormPath}/${formId}/calculate-and-save-offers`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};

export const calculateAndSaveOffersForCreditLine = async (
	formId: string,
	dto: {
		ticketPrice?: number;
		downPaymentAmount?: number;
		loanDuration?: number;
	},
): Promise<CreditLineOffer> => {
	try {
		const res = await post<CreditLineOffer>(
			`${baseFinancialFormPath}/credit-line/${formId}/calculate-and-save-offers`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};

export const createDocuments = async (formId: string) => {
	try {
		const res = await post<GenericFinancialForm>(
			`${baseFinancialFormPath}/${formId}/createDocuments`,
		);
		return res.data;
	} catch (err: any) {
		throw err.response.data;
	}
};

export const batchSellerAssign = async (
	financialForms: GenericFinancialForm[],
	seller: string,
): Promise<GenericFinancialForm[]> => {
	try {
		const res = await post('v1/sales/batch/assign-seller', {
			sellerId: seller,
			financialFormIds: financialForms.map((form) => form._id),
		});
		return res.data;
	} catch (err: any) {
		throw err.response.data.message;
	}
};

export const assignVehicle = async (
	formId: string,
): Promise<LoanFinancialFormWithExtraFields> => {
	try {
		const res = await post<LoanFinancialFormWithExtraFields>(
			`${baseFinancialFormPath}/${formId}/assign-vehicle`,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};
